import axios from 'axios';
import finalConfig from "configs/config";

const API_BASE_URL = finalConfig.TRANSACTION_API_BASE_URL;
const TRANSACTION_SERVICE_ID = finalConfig.TRANSACTION_SERVICE_ID;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token management - shared between UserContext and api.js
let idToken = null;
let accessToken = null;

// Token setters for UserContext to use
const setIdToken = (token) => {
  idToken = token;
};

const setAccessToken = (token) => {
  accessToken = token;
};

const getIdToken = () => idToken;
const getAccessToken = () => accessToken;

// Check if token is expired
const isTokenExpired = (token) => {
  if (!token) return true;
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp && payload.exp < currentTime;
  } catch (error) {
    console.error("Error checking token expiration:", error);
    return true;
  }
};

// Add token to requests
api.interceptors.request.use(
  async (config) => {
    if (idToken) {
      // Check if token is expired
      if (isTokenExpired(idToken)) {
        if (window.logoutUser) {
          window.logoutUser();
        } else {
          window.location.href = '/sign-in';
        }
        return Promise.reject(new Error("Token expired"));
      }
      
      config.headers.Authorization = `Bearer ${idToken}`;
      config.headers['X-Token-Type'] = 'wso2';
    } else {
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle token responses
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Check for token expired error
    if (error.response?.status === 401 && 
        error.response?.data?.detail === "Token expired") {
      
      // Clear tokens
      idToken = null;
      accessToken = null;
      
      // Trigger logout in UserContext
      if (window.logoutUser) {
        window.logoutUser();
      } else {
        // Fallback: redirect to sign-in
        window.location.href = '/sign-in';
      }
    }
    
    return Promise.reject(error);
  }
);

// Helper function to track user actions
const trackAction = async (actionType, description, inputData, outputData) => {
  try {
    await api.post('/credits/use', {
      action_type: actionType,
      description: description || `Performed ${actionType} operation`,
      input_data: inputData,
      output_data: outputData,
      transaction_service_id: TRANSACTION_SERVICE_ID
    });
  } catch (error) {
    // Don't throw error to avoid breaking the main API call
  }
};

// Helper function to check if user has enough credits before performing an action
const checkBalanceBeforeAction = async (actionType) => {
  try {
    // Get current balance
    const balanceResponse = await api.get('/credits/balance', {
      params: { transaction_service_id: TRANSACTION_SERVICE_ID }
    });
    const currentBalance = balanceResponse.data.credit_balance;
    
    // Get action credit cost
    const activitiesResponse = await api.get('/credits/activities', {
      params: { transaction_service_id: TRANSACTION_SERVICE_ID }
    });
    const action = activitiesResponse.data.activities.find(a => a.action_type === actionType);
    
    if (!action) {
      throw new Error(`Action type ${actionType} not found`);
    }
    
    const creditCost = action.credit_cost;
    
    if (currentBalance < creditCost) {
      return {
        hasEnoughCredits: false,
        currentBalance,
        requiredCredits: creditCost,
        shortfall: creditCost - currentBalance,
        actionName: action.name
      };
    }
    
    return {
      hasEnoughCredits: true,
      currentBalance,
      requiredCredits: creditCost,
      actionName: action.name
    };
  } catch (error) {
    console.error('Error checking balance:', error);  
    throw error;
  }
};

// Helper function to get credit cost for an action type
const getActionCreditCost = async (actionType) => {
  try {
    const activitiesResponse = await api.get('/credits/activities', {
      params: { transaction_service_id: TRANSACTION_SERVICE_ID }
    });
    const action = activitiesResponse.data.activities.find(a => a.action_type === actionType);
    return action ? action.credit_cost : null;
  } catch (error) {
    console.error('Error getting action credit cost:', error);
    return null;
  }
};

// Auth API - WSO2 token management
export const authAPI = {
  // WSO2 authentication endpoints
  getWSO2UserInfo: () => api.get('/wso2-auth/me'),
  getWSO2Profile: () => api.get('/wso2-auth/profile'),
  syncWSO2Profile: () => api.post('/wso2-auth/sync'),
  logout: () => api.post('/wso2-auth/logout', null, {
    params: { transaction_service_id: TRANSACTION_SERVICE_ID }
  }),
  validateToken: () => api.post('/wso2-auth/validate-token'),
  
  // Token setters and getters for UserContext
  setIdToken,
  setAccessToken,
  getIdToken,
  getAccessToken,
  
  // Clear tokens
  clearTokens: () => {
    idToken = null;
    accessToken = null;
  },
};

// Credits API
export const creditsAPI = {
  useCredits: (actionData) => api.post('/credits/use', {
    ...actionData,
    transaction_service_id: TRANSACTION_SERVICE_ID
  }),
  getBalance: () => api.get('/credits/balance', {
    params: { transaction_service_id: TRANSACTION_SERVICE_ID }
  }),
  getActions: (limit = 10, offset = 0) => api.get('/credits/actions', {
    params: { 
      transaction_service_id: TRANSACTION_SERVICE_ID, 
      limit,
      offset
    }
  }),
  getActionsCount: () => api.get('/credits/actions/count', {
    params: { transaction_service_id: TRANSACTION_SERVICE_ID }
  }),
  getActivities: () => api.get('/credits/activities', {
    params: { transaction_service_id: TRANSACTION_SERVICE_ID }
  }),
};

// Plans API
export const plansAPI = {
  getPlans: () => api.get('/plans', {
    params: { transaction_service_id: TRANSACTION_SERVICE_ID }
  }),
  getPlan: (planId) => api.get(`/plans/${planId}`, {
    params: { transaction_service_id: TRANSACTION_SERVICE_ID }
  }),
  subscribeToPlan: (planId) => api.post(`/plans/subscribe/${planId}`, null, {
    params: { transaction_service_id: TRANSACTION_SERVICE_ID }
  }),
  getMySubscriptions: () => api.get('/plans/subscriptions/my', {
    params: { transaction_service_id: TRANSACTION_SERVICE_ID }
  }),
  getMyTransactions: () => api.get('/plans/transactions/my', {
    params: { transaction_service_id: TRANSACTION_SERVICE_ID }
  }),
  getActiveSubscriptions: () => api.get('/plans/subscriptions/active', {
    params: { transaction_service_id: TRANSACTION_SERVICE_ID }
  }),
  cancelSubscription: (subscriptionId) => api.post(`/plans/subscriptions/${subscriptionId}/cancel`, null, {
    params: { transaction_service_id: TRANSACTION_SERVICE_ID }
  }),
  reactivateSubscription: (subscriptionId) => api.post(`/plans/subscriptions/${subscriptionId}/reactivate`, null, {
    params: { transaction_service_id: TRANSACTION_SERVICE_ID }
  }),
  afterCheckout: (sessionId) => api.get(`/plans/after-checkout?session_id=${sessionId}`),
};

// Health check
export const healthAPI = {
  check: () => api.get('/health'),
};

export { trackAction, checkBalanceBeforeAction, getActionCreditCost };
export default api; 
